import React, { useState } from 'react';

import {
    Checkbox,
    darken,
    lighten,
    Palette,
    SxProps,
    TableCell,
    TableRow,
    Typography,
    TypographyProps,
    useTheme,
} from '@mui/material';

import useSelectItem from '../FlexyDatatableTable/useSelectItem';
import { ColumnDatatable } from '../ColumnDatatable';
import { RowSelectionAction } from '../FlexyDatatableSelectionAction';
import { TruncatedCellContent } from '../TruncatedCellContent';

export const tableCellSx = (palette: Palette): SxProps => ({
    padding: '7px 8px',
    borderBottom: 'none',
    borderTopStyle: 'solid',
    borderTopWidth: '1px',
    borderTopColor: palette.mode === 'light' ? palette.grey[200] : palette.grey[800],
});

export type FlexyDatatableBodySuccededRowProps<T extends Record<string, unknown>> = {
    textCellVariant?: TypographyProps['variant'];
    onClickRow?: (e: React.MouseEvent<HTMLElement>, item?: T) => void;
    onWheelClickRow?: (e: React.MouseEvent<HTMLElement>, item?: T) => void;
    item: T;
    computeSxRow?: (item: T) => SxProps;
    computeSxRowHover?: (item: T) => SxProps;
    columns: Array<ColumnDatatable<T>>;
    rowsActions?: Array<RowSelectionAction>;
};

const removeDatatableIdentifier = (item: Record<string, unknown>) => {
    const newItem = { ...item };
    if ('datatableIdentifier' in newItem) {
        delete newItem.datatableIdentifier;
    }
    return newItem;
};

const hasFixedWidth = (columnSx?: SxProps): boolean => {
    if (!columnSx) return false;

    const sx = columnSx as Record<string, unknown>;
    return !!(sx.width || sx.maxWidth || sx.minWidth);
};

export const FlexyDatatableBodySuccededRow = function <T extends Record<string, unknown>>({
    textCellVariant,
    onClickRow,
    onWheelClickRow,
    item,
    rowsActions = [],
    columns,
    computeSxRow = () => null,
    computeSxRowHover = () => null,
}: FlexyDatatableBodySuccededRowProps<T>) {
    const selectItemHook = useSelectItem();
    const { palette } = useTheme();
    const [rowHover, setRowHover] = useState(false);
    const [hoveredCell, setHoveredCell] = useState<string | null>(null);

    // Type assertion for select item usage
    const itemWithId = item as T & { id: unknown };
    const isItemSelected = selectItemHook.isItemSelected(itemWithId);

    // Determine if row has any click action available
    const hasRowClickAction = !!(onClickRow || onWheelClickRow);

    const isLightMode = palette.mode === 'light';

    let bgColor = 'inherit';
    if (isItemSelected) {
        bgColor = isLightMode
            ? lighten(palette.primary.main, 0.85)
            : darken(palette.primary.main, 0.7);
    }

    const rowHoverBg = isLightMode ? palette.grey[100] : palette.grey[800];

    // Row hover styles with gray background
    const rowHoverSx = {
        cursor: hasRowClickAction ? 'pointer' : 'default',
        backgroundColor: isItemSelected ? bgColor : rowHoverBg,
        transition: 'background-color 0.2s ease',
        ...computeSxRowHover(item),
    };

    return (
        <TableRow
            sx={
                {
                    height: '70px',
                    backgroundColor: bgColor,
                    transition: 'all 0.2s ease',
                    '&:hover': rowHoverSx,
                    '--row-fade-color': rowHover ? rowHoverSx.backgroundColor : bgColor,
                    ...computeSxRow(item),
                } as SxProps
            }
            onMouseEnter={() => setRowHover(true)}
            onMouseLeave={() => {
                setRowHover(false);
                setHoveredCell(null);
            }}
        >
            {rowsActions.length > 0 && (
                <TableCell
                    key={`${item.datatableIdentifier}-select-item`}
                    sx={{
                        ...tableCellSx(palette),
                        width: '60px',
                        display: {
                            xs: 'none',
                            sm: 'none',
                            md: 'table-cell',
                            lg: 'table-cell',
                            xl: 'table-cell',
                        },
                    }}
                >
                    <Checkbox
                        checked={isItemSelected}
                        onChange={() => {
                            selectItemHook.handleSelectItem(itemWithId);
                        }}
                        size="small"
                        sx={{
                            opacity: isItemSelected || rowHover ? '1' : '0',
                        }}
                    />
                </TableCell>
            )}
            {columns.map((column, idx) => {
                const hasOnClickCell =
                    column.onClickCell !== undefined && typeof column.onClickCell === 'function';
                const hasOnClickRow = onClickRow && typeof onClickRow === 'function';

                const cellIdentifier = `${item.datatableIdentifier}-${column.label}`;
                const isCellHovered = hoveredCell === cellIdentifier;

                // Determine if content is text-only (in Typography)
                const isTextOnlyCell = ((): boolean => {
                    const content =
                        typeof column.render === 'string'
                            ? column.render in item && (item[column.render] as string)
                            : column.render(item);

                    // Truncated cells always use Typography internally, so they're text-only
                    const shouldTruncate = !hasFixedWidth(column.sx);
                    if (shouldTruncate) return true;

                    // For non-truncated content, check if it's text-only
                    return typeof content === 'string' || typeof content === 'number';
                })();

                // Determine cursor based on interaction availability
                const getCursor = () => {
                    if (hasOnClickCell) return 'pointer';
                    if (isTextOnlyCell) return 'default';
                    if (hasOnClickRow) return 'pointer';
                    return 'default';
                };

                return (
                    <TableCell
                        key={cellIdentifier}
                        onClick={(e) => {
                            if (hasOnClickCell) {
                                column.onClickCell?.(e, removeDatatableIdentifier(item) as T);
                                e.stopPropagation();
                            } else if (hasOnClickRow) {
                                onClickRow(e, removeDatatableIdentifier(item) as T);
                            }
                        }}
                        onAuxClick={(e) => {
                            const wheelClick = e.button === 1;

                            if (wheelClick && onWheelClickRow) {
                                onWheelClickRow(e, item);
                            }
                        }}
                        onMouseEnter={() => setHoveredCell(cellIdentifier)}
                        onMouseLeave={() => setHoveredCell(null)}
                        sx={
                            {
                                ...tableCellSx(palette),
                                paddingLeft: idx === 0 ? '15px' : undefined,
                                cursor: getCursor(),
                                transition: 'all 0.2s ease',
                                // Cell-specific hover effects (only for interactive cells)
                                ...(isCellHovered &&
                                    hasOnClickCell && {
                                        color: palette.primary.main,
                                        textShadow: isLightMode
                                            ? '0 1px 2px rgba(23, 55, 119, 0.18)'
                                            : '0 1px 2px rgba(0, 0, 0, 0.3)',
                                    }),
                                // Limit column width in case there is no fixed value
                                ...(hasFixedWidth(column.sx)
                                    ? {}
                                    : {
                                          maxWidth: '200px',
                                      }),
                                ...column.sx,
                            } as SxProps
                        }
                    >
                        {(() => {
                            const content =
                                typeof column.render === 'string'
                                    ? column.render in item && (item[column.render] as string)
                                    : column.render(item);

                            const shouldTruncate = !hasFixedWidth(column.sx);

                            if (shouldTruncate) {
                                return (
                                    <TruncatedCellContent
                                        content={content}
                                        textCellVariant={textCellVariant}
                                        columnRowReverse={column.rowReverse}
                                        isCellHovered={isCellHovered}
                                        hasOnClickCell={hasOnClickCell}
                                        rowHover={rowHover}
                                    />
                                );
                            }

                            return (
                                <Typography
                                    variant={textCellVariant || 'l2dcaption'}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: column.rowReverse ? 'row-reverse' : 'row',
                                        transition: 'color 0.3s ease, text-shadow 0.3s ease',
                                        // Apply hover effects for interactive
                                        ...(isCellHovered &&
                                            hasOnClickCell && {
                                                color: palette.primary.main,
                                                textShadow: isLightMode
                                                    ? '0 1px 2px rgba(23, 55, 119, 0.18)'
                                                    : '0 1px 2px rgba(0, 0, 0, 0.3)',
                                            }),
                                    }}
                                >
                                    {content}
                                </Typography>
                            );
                        })()}
                    </TableCell>
                );
            })}
        </TableRow>
    );
};
